import {
    Component,
    computed,
    effect,
    untracked,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { InvoiceDetail } from './types/InvoiceDetail';
import { IimCardModule } from '@iim-angular/components';
import { TranslateModule } from '@ngx-translate/core';
import { CopyToClipboardModule, DebugModule, IFileResponse, View } from '@lib/core';

import { DayjsModule, DisplayCurrencyModule, HighlightModule, InvoiceStatus } from '@lib/util';
import { InvoiceStatusDisplayComponent } from '@lib/invoice-public';
import { UnitModule } from '@lib/unit';
import { NavigationExtras, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    DocumentItemAccountingComponent,
    DocumentItemAttributeComponent,
    DocumentItemComponent,
    DocumentItemDescriptionComponent,
    DocumentItemLabelComponent,
    DocumentItemNameComponent,
    DocumentItemPositionIndicatorComponent,
    DocumentItemPriceComponent,
    DocumentItemValueComponent,
    DocumentLayoutComponent,
    DocumentSidemenuAttachmentComponent,
    DocumentSidemenuCardComponent,
    DocumentSidemenuSectionComponent,
    DocumentSidemenuSectionEmptyMessageComponent,
    DocumentSidemenuSectionHeaderComponent,
} from '@lib/document';
import { AccountingChange } from '@lib/accounting/types';
import { AccountingSelectionModule, AccountingSelectionService } from '@lib/accounting/selection';
import { InvoiceActivityComponent } from '../activity/invoice-activity.component';
import { InvoiceProcessStatusIndicatorComponent } from './process-status-indicator/invoice-process-status-indicator.component';
import { CompanyAddress, CompanyAddressModule } from '@lib/address';
import { InvoiceApproverOverviewComponent } from '../approve/overview/invoice-approver-overview.component';
import { AccountingDataRequest } from './types/AccountingDataRequest';
import { InvoiceDetailItem } from './types/IInvoiceDetail';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InvoiceDataService } from '../invoice-data.service';
import {
    ProcessChainDataService,
    ProcessChainDocumentType,
    ProcessChainRelatedDocument,
    ProcessChainRelatedDocumentComponent,
} from '@lib/process-chain';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { MaterialGroupDataRequest } from './types/MaterialGroupDataRequest';
import { MaterialGroupSelectionComponent } from '@lib/material-group/selection';
import { InvoiceSettingsDto } from '../types/InvoiceSettingsDto';
import { CHARGE_TYPE_LABELS } from '../create-standalone/types/ChargeType.enum';
import { SupplierDisplayNamePipe } from '@lib/supplier';
import { InvoiceItemOrderComparisonComponent } from './comparison/invoice-item-order-comparison.component';
import { InvoiceOrderComparison } from './types/InvoiceOrderComparison';

@Component({
    selector: 'invoice-detail',
    imports: [
        CommonModule,
        IimCardModule,
        TranslateModule,
        CopyToClipboardModule,
        DebugModule,
        DayjsModule,
        InvoiceStatusDisplayComponent,
        HighlightModule,
        UnitModule,
        RouterLink,
        DocumentItemComponent,
        DocumentItemPositionIndicatorComponent,
        DocumentItemNameComponent,
        DocumentItemDescriptionComponent,
        DocumentItemPriceComponent,
        DocumentItemLabelComponent,
        DocumentItemAttributeComponent,
        DocumentItemValueComponent,
        DocumentLayoutComponent,
        DocumentSidemenuSectionComponent,
        DocumentSidemenuSectionHeaderComponent,
        DocumentSidemenuAttachmentComponent,
        DocumentSidemenuCardComponent,
        DocumentSidemenuSectionEmptyMessageComponent,
        AccountingSelectionModule,
        DocumentItemAccountingComponent,
        InvoiceActivityComponent,
        InvoiceProcessStatusIndicatorComponent,
        CompanyAddressModule,
        InvoiceApproverOverviewComponent,
        MatTooltipModule,
        ProcessChainRelatedDocumentComponent,
        MaterialGroupSelectionComponent,
        DisplayCurrencyModule,
        SupplierDisplayNamePipe,
        InvoiceItemOrderComparisonComponent,
    ],
    templateUrl: './invoice-detail.component.html',
    styleUrl: './invoice-detail.component.scss',
})
export class InvoiceDetailComponent {
    private readonly _accountSelection = inject(AccountingSelectionService);
    private readonly _invoiceDataService = inject(InvoiceDataService);
    private readonly _processChainDataService = inject(ProcessChainDataService);
    private readonly _companyCodeDataService = inject(CompanyCodeDataService);

    public readonly invoice: InputSignal<InvoiceDetail> = input.required();
    public readonly taskId: InputSignal<string> = input();
    public readonly forceMobileLayout: InputSignal<boolean> = input(false);
    public readonly filesChanged: OutputEmitterRef<IFileResponse[]> = output<IFileResponse[]>();
    public readonly accountingSelected: OutputEmitterRef<AccountingDataRequest> = output<AccountingDataRequest>();
    public readonly materialGroupSelected: OutputEmitterRef<MaterialGroupDataRequest> = output<MaterialGroupDataRequest>();
    public readonly onAllAccountingsSelected: OutputEmitterRef<void> = output<void>();
    public readonly accountingValidationChanged: OutputEmitterRef<boolean> = output<boolean>();
    public readonly accountingWarningsChanged: OutputEmitterRef<readonly string[]> = output<readonly string[]>();

    private readonly processStatusIndicatorComponent = viewChild(InvoiceProcessStatusIndicatorComponent);
    private readonly accountingValidMap = new Map<string, boolean>();
    private readonly materialGroupValidMap = new Map<string, boolean>();
    private readonly accountingWarningsByItem = new Map<string, readonly string[]>();
    private readonly invoiceSettings = signal<InvoiceSettingsDto | null>(null);
    private readonly orderComparison = signal<InvoiceOrderComparison | null>(null);
    protected readonly itemComparisons = computed(() => this.orderComparison()?.itemsById ?? {});
    protected readonly deviationsByOrderId = computed(() => this.orderComparison()?.deviationsByOrderId ?? {});
    protected readonly isCreditNote = computed(() => this.orderComparison()?.creditNote ?? false);

    protected readonly invoiceStatus = InvoiceStatus;
    protected readonly chargeTypeLabels = CHARGE_TYPE_LABELS;
    private readonly fetchedLinkedDocuments = signal<ProcessChainRelatedDocument[]>([]);
    protected readonly linkedProcessDocuments = computed<ProcessChainLinkedDocumentView[]>(() =>
        this.fetchedLinkedDocuments().map((document) => ({
            ...document,
            targetRoute: this.resolveLinkedDocumentRoute(document),
        })),
    );
    public recommendedMaterialGroupCodesByItemId: Record<string, readonly string[]> = {};
    public recommendedMaterialGroupSourceLabelsByItemId: Record<string, string | null> = {};
    public recommendedMaterialGroupReasonLabelsByItemId: Record<string, string | null> = {};
    protected readonly companyCodes: Signal<ICompanyCode[]> = toSignal(this._companyCodeDataService.getMyActive(), {
        initialValue: [],
    });
    private readonly fetchedCompanyCode = signal<ICompanyCode | null>(null);
    protected readonly companyCodeDisplay = computed(() => {
        const invoice = this.invoice();
        const companyCodeId = invoice?.companyCodeId;
        if (!companyCodeId) {
            return '';
        }
        const matching =
            this.companyCodes().find((code) => code.id === companyCodeId) ??
            (this.fetchedCompanyCode()?.id === companyCodeId ? this.fetchedCompanyCode() : null);
        if (!matching) {
            return companyCodeId;
        }
        return matching.name ? `${matching.code} (${matching.name})` : matching.code;
    });
    protected readonly showCompanyCode = computed(
        () => this.companyCodes().length > 1 && !!this.companyCodeDisplay(),
    );

    public readonly invoiceAddress: Signal<CompanyAddress | undefined> = computed(() => {
        const invoice = this.invoice();

        return invoice.invoiceAddress
            ? CompanyAddress.fromCompanyAddressDto(invoice.invoiceAddress.companyAddress)
            : undefined;
    });

    public readonly hasTask = computed(() => !!this.taskId()?.length);
    public readonly isReceiptBasedInvoice = computed(() => this.invoice().receiptRelations.length > 0);
    public readonly isTaskEditingAllowed = computed(() => {
        const currentInvoice = this.invoice();
        return currentInvoice.status === this.invoiceStatus.UNAPPROVED && this.hasTask();
    });
    public readonly taskAccountingReadOnly = computed(() => {
        if (!this.hasTask() || !this.isReceiptBasedInvoice()) {
            return false;
        }

        return !(this.invoiceSettings()?.receiptBasedAccountingEditable ?? true);
    });

    public readonly isAccountingEditable = computed(() => {
        return this.isTaskEditingAllowed() && !this.taskAccountingReadOnly();
    });
    public readonly isMaterialGroupEditable = computed(() => this.isTaskEditingAllowed());

    public readonly supplierAddress: Signal<CompanyAddress | undefined> = computed(() => {
        const invoice = this.invoice();

        return invoice.supplierAddress ? CompanyAddress.fromCompanyAddressDto(invoice.supplierAddress) : undefined;
    });

    private readonly _router = inject(Router);

    constructor() {
        effect(() => {
            const invoice = this.invoice();
            if (invoice && this.processStatusIndicatorComponent()) {
                // reload process status indicator when invoice changes
                this.processStatusIndicatorComponent().ngOnInit();
            }
            this.accountingValidMap.clear();
            this.materialGroupValidMap.clear();
            this.accountingWarningsByItem.clear();
            this.recommendedMaterialGroupCodesByItemId = {};
            this.recommendedMaterialGroupSourceLabelsByItemId = {};
            this.recommendedMaterialGroupReasonLabelsByItemId = {};
        });

        effect((onCleanup) => {
            const invoice = this.invoice();
            if (!invoice) {
                this.fetchedLinkedDocuments.set([]);
                return;
            }

            const subscription = this._processChainDataService
                .getRelatedDocuments(ProcessChainDocumentType.INVOICE, invoice.id)
                .subscribe({
                    next: (relatedDocuments) => this.fetchedLinkedDocuments.set(relatedDocuments),
                    error: (error) => console.error('Failed to load related documents', error),
                });

            onCleanup(() => subscription.unsubscribe());
        });

        effect((onCleanup) => {
            const companyCodeId = this.invoice()?.companyCodeId;
            if (!companyCodeId) {
                this.fetchedCompanyCode.set(null);
                return;
            }

            const hasCompanyCodeInActiveList = this.companyCodes().some((companyCode) => companyCode.id === companyCodeId);
            if (hasCompanyCodeInActiveList || this.fetchedCompanyCode()?.id === companyCodeId) {
                return;
            }

            const subscription = this._companyCodeDataService.getById(companyCodeId).subscribe({
                next: (companyCode) => this.fetchedCompanyCode.set(companyCode),
                error: () => this.fetchedCompanyCode.set(null),
            });
            onCleanup(() => subscription.unsubscribe());
        });

        effect((onCleanup) => {
            if (!this.hasTask()) {
                this.invoiceSettings.set(null);
                return;
            }

            const subscription = this._invoiceDataService.getInvoiceSettings().subscribe({
                next: (settings) => this.invoiceSettings.set(settings),
                error: () => this.invoiceSettings.set(null),
            });

            onCleanup(() => subscription.unsubscribe());
        });

        // Deviations of the invoice from its orders (UC_2026). Keyed on the invoice INSTANCE, not on
        // its id: reloading the approval task yields a new InvoiceDetail with the same id, and an
        // id-keyed trigger would leave the pre-edit deviations on screen right before release.
        effect((onCleanup) => {
            const invoice = this.invoice();
            const taskId = this.taskId();

            untracked(() => {
                // Only a different invoice invalidates what is on screen. The approval form rebuilds
                // the InvoiceDetail after every accounting or material-group save, and blanking here
                // would make every marker and every conditional row vanish and reappear each time.
                if (this.orderComparison()?.invoiceId !== invoice?.id) {
                    this.orderComparison.set(null);
                }

                // No stored order link means nothing to compare, and nothing left over from an earlier
                // state of this invoice either: after the last link is removed the invoice comes back
                // under the same id, and a comparison kept by id alone would still show the block of a
                // position that is no longer linked.
                if (!invoice || !invoice.items.some((item) => item.orderId != null)) {
                    this.orderComparison.set(null);
                    return;
                }

                const subscription = this._invoiceDataService.getOrderComparison(invoice.id, taskId).subscribe({
                    next: (comparison) => this.orderComparison.set(comparison),
                    error: (error) => console.error('Failed to load order comparison', error),
                });

                onCleanup(() => subscription.unsubscribe());
            });
        });
    }

    navigateToReceipt(receiptId: number, receiptNumber: string) {
        this._router.navigate([View.CLIENT, 'order', 'receipt', receiptId], {
            queryParams: { receiptNumber: receiptNumber },
        });
    }

    changeAccounting(accountingChangeData: AccountingChange, invoiceItemId: string, invoiceId: string) {
        this.accountingSelected.emit({
            accountingChangeData,
            invoiceItemId,
            invoiceId,
        });
    }

    changeMaterialGroup(materialGroupCode: string | undefined, invoiceItemId: string, invoiceId: string) {
        this.materialGroupSelected.emit({
            materialGroupCode,
            invoiceItemId,
            invoiceId,
        });
    }

    setAllAccountings(): void {
        this._accountSelection.openAccountingChangeDialog({
            editCostCenter: true,
            editProject: true,
            editGeneralLedgerAccount: true,
            editCustomAccountAssignment: true,
            companyCodeId: this.invoice().companyCodeId ?? '',
            // EPROC-11017: invoices have neither classification nor site bulk support.
            hideClassification: true,
            hideSite: true,
        }).subscribe((accountingChanges) => {
            if (!accountingChanges || accountingChanges.length === 0) {
                return;
            }

            const items: InvoiceDetailItem[] = this.invoice().items;
            if (items.length <= 0) {
                return;
            }

            this._invoiceDataService.setAllAccountingValues(accountingChanges, this.invoice().id).subscribe({
                next: () => {
                    this.onAllAccountingsSelected.emit();
                },
                error: (err) => console.error('PUT failed', err),
            });
        });
    }

    onAccountingValidationChange(isValid: boolean, invoiceItemId: string): void {
        this.accountingValidMap.set(invoiceItemId, isValid);
        this.emitCombinedItemValidationState();
    }

    onAccountingWarningsChange(warnings: readonly string[], invoiceItemId: string): void {
        this.accountingWarningsByItem.set(invoiceItemId, warnings);
        const seen = new Set<string>();
        this.accountingWarningsByItem.forEach((perItem) => perItem.forEach((message) => seen.add(message)));
        this.accountingWarningsChanged.emit(Array.from(seen));
    }

    onMaterialGroupValidationChange(isValid: boolean, invoiceItemId: string): void {
        this.materialGroupValidMap.set(invoiceItemId, isValid);
        this.emitCombinedItemValidationState();
    }

    updateMaterialGroupCandidates(invoiceItemId: string, candidateIds: readonly string[]): void {
        this.recommendedMaterialGroupCodesByItemId = {
            ...this.recommendedMaterialGroupCodesByItemId,
            [invoiceItemId]: candidateIds,
        };
    }

    updateMaterialGroupRecommendationSourceLabel(invoiceItemId: string, sourceLabel: string | null): void {
        this.recommendedMaterialGroupSourceLabelsByItemId = {
            ...this.recommendedMaterialGroupSourceLabelsByItemId,
            [invoiceItemId]: sourceLabel,
        };
    }

    updateMaterialGroupRecommendationReasonLabel(invoiceItemId: string, reasonLabel: string | null): void {
        this.recommendedMaterialGroupReasonLabelsByItemId = {
            ...this.recommendedMaterialGroupReasonLabelsByItemId,
            [invoiceItemId]: reasonLabel,
        };
    }

    navigateToLinkedDocument(linkedDocument: ProcessChainLinkedDocumentView): void {
        if (!linkedDocument.targetRoute) {
            return;
        }

        const extras = this.buildNavigationExtras(linkedDocument);
        if (extras) {
            void this._router.navigate(linkedDocument.targetRoute, extras);
        } else {
            void this._router.navigate(linkedDocument.targetRoute);
        }
    }

    private resolveLinkedDocumentRoute(linkedDocument: ProcessChainRelatedDocument): Array<string | number> | null {
        switch (linkedDocument.documentType) {
            case ProcessChainDocumentType.ORDER:
                return [View.CLIENT, 'order', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.DELIVERY_RECEIPT:
                return [View.CLIENT, 'order', 'receipt', linkedDocument.documentId];
            case ProcessChainDocumentType.ORDER_CONFIRMATION:
                return [View.CLIENT, 'order', 'confirmation', linkedDocument.documentId];
            case ProcessChainDocumentType.INVOICE:
                return [View.CLIENT, 'my-admin', 'invoice', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.PURCHASE_REQUISITION:
                return [View.CLIENT, 'rfq', 'purchase-requisition', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.RFQ:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'detail'];
            case ProcessChainDocumentType.QUOTATION:
                return [View.CLIENT, 'rfq', linkedDocument.documentId, 'quotation'];
            default:
                return null;
        }
    }

    private buildNavigationExtras(linkedDocument: ProcessChainRelatedDocument): NavigationExtras | undefined {
        if (
            linkedDocument.documentType === ProcessChainDocumentType.DELIVERY_RECEIPT &&
            linkedDocument.documentNumber
        ) {
            return { queryParams: { receiptNumber: linkedDocument.documentNumber } };
        }

        return undefined;
    }

    private emitCombinedItemValidationState(): void {
        const allAccountingValid = Array.from(this.accountingValidMap.values()).every((value) => value);
        const allMaterialGroupsValid = Array.from(this.materialGroupValidMap.values()).every((value) => value);
        this.accountingValidationChanged.emit(allAccountingValid && allMaterialGroupsValid);
    }
}

type ProcessChainLinkedDocumentView = ProcessChainRelatedDocument & {
    readonly targetRoute: Array<string | number> | null;
};
